/**
 * Layout and display primitives shared by every app screen.
 *
 * Styling is inline-object rather than SCSS on purpose: nearly every colour here
 * is computed from a measured value at render time (zone scales, engine
 * inversion), so a stylesheet would only hold the handful of values that never
 * move. Those live in `tokens.ts`.
 */
import React from 'react';
import { useT, useTt } from '../i18n/useT';
import { Icon } from './icons';
import {
    c,
    hair,
    hair2,
    f,
    ACT,
    scoreColor,
    patternZone,
    strengthKey,
    effectiveStrength,
    SCORE_ZONES,
    DAY_KEYS,
    Polarity,
} from './tokens';

type Div = React.HTMLAttributes<HTMLDivElement>;

export const Row: React.FC<Div & { gap?: number }> = ({ children, style, gap, ...rest }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap, ...style }} {...rest}>
        {children}
    </div>
);

export const Col: React.FC<Div & { gap?: number }> = ({ children, style, gap, ...rest }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap, ...style }} {...rest}>
        {children}
    </div>
);

const metaBase: React.CSSProperties = {
    fontFamily: f.mono,
    fontSize: 11,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
};

export const Eyebrow: React.FC<{ children: React.ReactNode; color?: string; style?: React.CSSProperties }> = ({
    children,
    color,
    style,
}) => <div style={{ ...metaBase, color: color || c.muted, ...style }}>{children}</div>;

export const Meta: React.FC<{ children: React.ReactNode; color?: string; style?: React.CSSProperties }> = ({
    children,
    color,
    style,
}) => <span style={{ ...metaBase, color: color || c.muted, ...style }}>{children}</span>;

export const Chip: React.FC<{
    children: React.ReactNode;
    color?: string;
    border?: string;
    style?: React.CSSProperties;
}> = ({ children, color, border, style }) => (
    <span
        style={{
            ...metaBase,
            color: color || c.muted,
            border: `1px solid ${border || 'rgba(232,230,224,0.10)'}`,
            borderRadius: 99,
            padding: '5px 10px',
            whiteSpace: 'nowrap',
            ...style,
        }}
    >
        {children}
    </span>
);

export const Card: React.FC<Div & { accent?: string }> = ({ children, style, accent, ...rest }) => (
    <div
        style={{
            background: c.card,
            border: accent ? `1px solid ${accent}` : hair,
            borderRadius: 16,
            padding: '15px 16px',
            ...style,
        }}
        {...rest}
    >
        {children}
    </div>
);

export const Sec: React.FC<{
    title: string;
    right?: React.ReactNode;
    children: React.ReactNode;
    style?: React.CSSProperties;
    bt?: boolean;
}> = ({ title, right, children, style, bt }) => (
    <section style={{ padding: '20px 20px 0', borderTop: bt ? hair : 'none', ...style }}>
        <Row style={{ justifyContent: 'space-between', marginBottom: 13 }}>
            <Eyebrow>{title}</Eyebrow>
            {right}
        </Row>
        {children}
    </section>
);

export const H1: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <h1
        style={{
            fontSize: 31,
            fontWeight: 300,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            margin: 0,
            color: c.textPri,
            ...style,
        }}
    >
        {children}
    </h1>
);

export const Body: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <p style={{ fontSize: 14, lineHeight: 1.6, color: c.text, fontWeight: 300, margin: 0, ...style }}>{children}</p>
);

export const Muted: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <p style={{ fontSize: 13, lineHeight: 1.55, color: c.muted, fontWeight: 300, margin: 0, ...style }}>{children}</p>
);

export const Btn: React.FC<{
    children: React.ReactNode;
    primary?: boolean;
    ic?: string;
    onClick?: () => void;
    style?: React.CSSProperties;
}> = ({ children, primary, ic, onClick, style }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
            width: '100%',
            height: 48,
            borderRadius: 99,
            cursor: 'pointer',
            background: primary ? c.textPri : 'transparent',
            border: primary ? 'none' : hair,
            color: primary ? '#0a0a0a' : c.textPri,
            fontFamily: f.ui,
            fontSize: 14.5,
            fontWeight: 500,
            ...style,
        }}
    >
        {ic && <Icon k={ic} size={16} color={primary ? '#0a0a0a' : c.textPri} />}
        {children}
    </button>
);

export const Delta: React.FC<{ v: number }> = ({ v }) => {
    const up = v >= 0;

    return (
        <span style={{ fontFamily: f.mono, fontSize: 11.5, color: up ? '#84cc16' : '#ef4444' }}>
            {up ? '↑' : '↓'} {Math.abs(v)}
        </span>
    );
};

export const PolTag: React.FC<{ pol: Polarity }> = ({ pol }) => {
    const t = useT();
    const isEngine = pol === 'engine';
    const col = isEngine ? '#4ade80' : '#fb923c';

    return (
        <span
            style={{
                fontFamily: f.mono,
                fontSize: 9.5,
                letterSpacing: '0.14em',
                color: col,
                border: `1px solid ${col}44`,
                background: `${col}12`,
                borderRadius: 99,
                padding: '3px 8px',
                whiteSpace: 'nowrap',
            }}
        >
            {t(isEngine ? 'app.pol.engine' : 'app.pol.shadow')}
        </span>
    );
};

export const NewTag: React.FC<{ hex?: string }> = ({ hex = '#f4f2ee' }) => {
    const t = useT();

    return (
        <span
        style={{
            fontFamily: f.mono,
            fontSize: 9,
            letterSpacing: '0.12em',
            color: '#0a0a0a',
            background: hex,
                borderRadius: 4,
                padding: '2px 5px',
            }}
        >
            {t('app.tag.new')}
        </span>
    );
};

/** Segmented bar in an explicit colour. `pct === 0` leaves every segment empty. */
export const SegBarC: React.FC<{ pct: number; hex: string; n?: number }> = ({ pct, hex, n = 10 }) => {
    const on = pct <= 0 ? 0 : Math.max(1, Math.round((pct / 100) * n));
    const segments = Array.from({ length: n }, (_, i) => ({ id: `seg-${i}`, lit: i < on }));

    return (
        <Row style={{ gap: 3, flex: 1 }}>
            {segments.map((s) => (
                <div
                    key={s.id}
                    style={{
                        flex: 1,
                        height: 5,
                        borderRadius: 2,
                        background: s.lit ? hex : c.track,
                        boxShadow: s.lit ? `0 0 5px ${hex}55` : 'none',
                    }}
                />
            ))}
        </Row>
    );
};

/** Dashed placeholder used wherever a bar would imply a number we do not have. */
export const SegBarEmpty: React.FC = () => (
    <div style={{ flex: 1, height: 5, borderRadius: 3, border: '1px dashed rgba(232,230,224,0.2)' }} />
);

export const PatLegend: React.FC = () => {
    const t = useT();

    return (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: hair2 }}>
            <Row style={{ gap: 12, flexWrap: 'wrap' }}>
                {[
                    ['app.zone.excellent', '#4ade80'],
                    ['app.zone.good', '#facc15'],
                    ['app.zone.fair', '#fb923c'],
                    ['app.zone.critical', '#f87171'],
                ].map(([key, col]) => (
                    <Row key={key} style={{ gap: 6 }}>
                        <span style={{ width: 12, height: 3, borderRadius: 2, background: col }} />
                        <Meta style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'lowercase' }}>
                            {t(key)}
                        </Meta>
                    </Row>
                ))}
            </Row>
        </div>
    );
};

/**
 * Accordion. Animates via `grid-template-rows: 0fr → 1fr` so the panel does not
 * need a measured height; the inner wrapper keeps `overflow:hidden` so the
 * collapsed row is genuinely zero-height and unreachable by tab.
 */
export const Acc: React.FC<{
    title: string;
    meta?: string;
    children: React.ReactNode;
    open?: boolean;
    hex?: string;
    last?: boolean;
}> = ({ title, meta, children, open: initial = false, hex = c.textPri, last }) => {
    const [open, setOpen] = React.useState(initial);

    return (
        <div style={{ borderTop: hair, borderBottom: last ? hair : 'none' }}>
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((x) => !x)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '16px 0',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: f.ui,
                }}
            >
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, color: c.textPri, fontWeight: 400, letterSpacing: '-0.01em' }}>
                        {title}
                    </div>
                    {meta && <div style={{ fontSize: 12, color: c.muted, marginTop: 4, fontWeight: 300 }}>{meta}</div>}
                </div>
                <div
                    style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        border: hair,
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                        background: open ? `${hex}14` : 'transparent',
                        transition: 'background 350ms',
                    }}
                >
                    <Icon
                        k="down"
                        size={12}
                        color={open ? hex : c.muted}
                        style={{
                            transform: `rotate(${open ? 180 : 0}deg)`,
                            transition: 'transform 350ms cubic-bezier(.4,0,.2,1)',
                        }}
                    />
                </div>
            </button>
            <div
                style={{
                    display: 'grid',
                    gridTemplateRows: open ? '1fr' : '0fr',
                    transition: 'grid-template-rows 420ms cubic-bezier(.4,0,.2,1)',
                }}
            >
                <div style={{ overflow: 'hidden' }}>
                    <div
                        style={{
                            paddingBottom: 20,
                            opacity: open ? 1 : 0,
                            transform: open ? 'none' : 'translateY(-6px)',
                            transition: 'opacity 300ms 60ms, transform 300ms 60ms',
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Instant tooltip. The native `title` needs a second of hovering and never
 * shows on touch, so this one opens on hover AND toggles on tap/click.
 */
export const Hint: React.FC<{
    text: string;
    /** Which edge of the trigger the bubble grows from. */
    align?: 'left' | 'right';
    children: React.ReactNode;
}> = ({ text, align = 'left', children }) => {
    const [on, setOn] = React.useState(false);

    return (
        <button
            type="button"
            style={{
                position: 'relative',
                display: 'inline-flex',
                background: 'transparent',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                textAlign: 'inherit',
                cursor: 'help',
            }}
            onMouseEnter={() => setOn(true)}
            onMouseLeave={() => setOn(false)}
            onFocus={() => setOn(true)}
            onBlur={() => setOn(false)}
            onClick={() => setOn((v) => !v)}
        >
            {children}
            {on && (
                <span
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 8px)',
                        ...(align === 'left' ? { left: 0 } : { right: 0 }),
                        zIndex: 30,
                        width: 220,
                        background: '#1c1b19',
                        border: hair,
                        borderRadius: 10,
                        padding: '9px 11px',
                        fontSize: 11.5,
                        lineHeight: 1.45,
                        color: c.text,
                        fontFamily: f.ui,
                        letterSpacing: 'normal',
                        textTransform: 'none',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
                        pointerEvents: 'none',
                    }}
                >
                    {text}
                </span>
            )}
        </button>
    );
};

/** The four score zones rendered as a level ladder, with the current one raised. */
export const ZoneLevels: React.FC<{ score: number; style?: React.CSSProperties }> = ({ score, style }) => {
    const t = useT();
    const idx = Math.max(
        0,
        SCORE_ZONES.findIndex((z) => score < z.max),
    );
    const cur = SCORE_ZONES[idx];
    const range = idx === 0 ? '0–39' : `${SCORE_ZONES[idx - 1].max}–${cur.max - 1}`;

    return (
        <Col style={{ padding: '0 20px', ...style }} gap={9}>
            <Row style={{ gap: 5 }}>
                {SCORE_ZONES.map((z, i) => (
                    <div
                        key={z.key}
                        style={{
                            flex: 1,
                            height: i === idx ? 7 : 4,
                            borderRadius: 3,
                            background: i === idx ? z.hex : `${z.hex}33`,
                            boxShadow: i === idx ? `0 0 8px ${z.hex}66` : 'none',
                            transition: 'height 300ms',
                        }}
                    />
                ))}
            </Row>
            <Row style={{ justifyContent: 'space-between' }}>
                <Meta style={{ fontSize: 10, color: cur.hex, letterSpacing: '0.14em' }}>{t(cur.key)}</Meta>
                <Meta style={{ fontSize: 10, color: c.subtle }}>{range}</Meta>
            </Row>
        </Col>
    );
};

/** Seven-day trend with per-bar numbers and an optional dashed threshold line. */
export const TrendWeek: React.FC<{
    data: number[];
    /** Monday-first index of today; bars after it are days that have not happened. */
    todayIdx?: number;
    /** Bar colour. Without it each bar is coloured by the score scale — only right when the data IS a score. */
    hex?: string;
    threshold?: number;
    thLabel?: string;
    footL: string;
    footR: string;
}> = ({ data, todayIdx, hex, threshold, thLabel, footL, footR }) => {
    const t = useT();
    const H = 92;
    const max = Math.max(threshold || 0, ...data) * 1.14 || 1;
    const today = todayIdx ?? data.length - 1;
    const bars = data.map((v, i) => ({ id: `bar-${i}`, v, today: i === today }));

    return (
        <div>
            <div style={{ position: 'relative', height: H, marginTop: 4 }}>
                {threshold != null && (
                    <>
                        <div
                            style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: `${(threshold / max) * 100}%`,
                                borderTop: '1px dashed rgba(232,230,224,0.16)',
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                left: 0,
                                bottom: `${(threshold / max) * 100}%`,
                                transform: 'translateY(-3px)',
                                background: c.bg,
                                paddingRight: 5,
                            }}
                        >
                            <Meta style={{ fontSize: 8.5, letterSpacing: '0.12em', color: c.subtle }}>{thLabel}</Meta>
                        </div>
                    </>
                )}
                <Row style={{ position: 'absolute', inset: 0, gap: 7, alignItems: 'flex-end' }}>
                    {bars.map((b) => {
                        const z = hex || scoreColor(b.v).hex;
                        let numColor: string = c.muted;

                        if (b.v === 0) numColor = c.subtle;
                        else if (b.today) numColor = c.textPri;

                        return (
                            <Col
                                key={b.id}
                                style={{ flex: 1, alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}
                                gap={5}
                            >
                                <span
                                    style={{
                                        fontFamily: f.mono,
                                        fontSize: 9.5,
                                        color: numColor,
                                    }}
                                >
                                    {b.v || '–'}
                                </span>
                                {b.v === 0 ? (
                                    <div
                                        style={{
                                            width: '100%',
                                            height: 5,
                                            borderRadius: 3,
                                            border: '1px dashed rgba(232,230,224,0.2)',
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: '100%',
                                            height: `${(b.v / max) * (H - 18)}px`,
                                            borderRadius: 5,
                                            background: b.today ? z : `${z}66`,
                                            boxShadow: b.today ? `0 0 10px ${z}55` : 'none',
                                        }}
                                    />
                                )}
                            </Col>
                        );
                    })}
                </Row>
            </div>
            <Row style={{ gap: 7, marginTop: 7 }}>
                {DAY_KEYS.map((key, i) => (
                    <Col key={key} style={{ flex: 1, alignItems: 'center' }}>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 9,
                                letterSpacing: '0.06em',
                                color: i === today ? c.textPri : c.subtle,
                                background: i === today ? 'rgba(232,230,224,0.08)' : 'transparent',
                                borderRadius: 99,
                                padding: i === today ? '2px 7px' : '2px 0',
                            }}
                        >
                            {t(key)}
                        </span>
                    </Col>
                ))}
            </Row>
            <Row style={{ marginTop: 13, paddingTop: 12, borderTop: hair2, justifyContent: 'space-between' }}>
                <Meta style={{ fontSize: 10 }}>{footL}</Meta>
                <Meta style={{ fontSize: 10, color: c.textPri }}>{footR}</Meta>
            </Row>
        </div>
    );
};

export const PatternRow: React.FC<{
    name: string;
    sub: string;
    pol: Polarity;
    pct: number | null;
    /** Points completed tasks have paid into this pattern. */
    earned?: number;
    isNew?: boolean;
    onClick?: () => void;
    style?: React.CSSProperties;
}> = ({ name, sub, pol, pct, earned, isNew, onClick, style }) => {
    const { t, tn } = useTt();
    const measured = pct != null;
    const pz = measured ? patternZone(pct as number, pol) : null;
    // Interactive rows become real buttons so keyboard and screen readers get
    // the same affordance the pointer does.
    const Tag = (onClick ? 'button' : 'div') as 'button' | 'div';
    const interactive = onClick ? ({ type: 'button' as const, onClick } as const) : {};

    return (
        <Tag
            {...interactive}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                font: 'inherit',
                color: 'inherit',
                padding: '13px 0',
                cursor: onClick ? 'pointer' : 'default',
                ...style,
            }}
        >
            <Row style={{ gap: 9 }}>
                {isNew && pz && <NewTag hex={pz.hex} />}
                <span style={{ fontSize: 14.5, color: c.textPri, fontWeight: 400, letterSpacing: '-0.015em', flex: 1 }}>
                    {name}
                </span>
                <PolTag pol={pol} />
                <Icon k="chev" size={12} color={c.subtle} />
            </Row>
            <Muted style={{ fontSize: 12, marginTop: 6 }}>{sub}</Muted>
            <Row style={{ gap: 12, marginTop: 11 }}>
                {/* Bar length uses the inverted value, same as the colour does —
                    a raw pct here would make a weak engine read long and green-
                    adjacent while its verdict says the opposite. */}
                {measured ? (
                    <SegBarC pct={effectiveStrength(pct as number, pol)} hex={(pz as { hex: string }).hex} />
                ) : (
                    <SegBarEmpty />
                )}
                <span
                    style={{
                        fontFamily: f.mono,
                        fontSize: 12.5,
                        color: measured ? c.textPri : c.subtle,
                        width: 24,
                        textAlign: 'right',
                    }}
                >
                    {measured ? Math.round(pct as number) : '–'}
                </span>
            </Row>
            <Row style={{ justifyContent: 'space-between', marginTop: 7 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.1em', color: pz ? pz.hex : c.subtle }}>
                    {measured ? t(strengthKey(pct as number, pol)) : t('app.pattern.collecting')}
                </Meta>
                {!!earned && (
                    <Meta style={{ fontSize: 9, letterSpacing: '0.08em', color: ACT }}>
                        +{tn('app.state.points', earned)}
                    </Meta>
                )}
            </Row>
        </Tag>
    );
};

/** Screen-entry transition. Children stagger in; respects reduced-motion. */
export const Appear: React.FC<{ children: React.ReactNode; delay?: number; style?: React.CSSProperties }> = ({
    children,
    delay = 0,
    style,
}) => {
    const reduced = usePrefersReducedMotion();
    const [on, setOn] = React.useState(reduced);

    React.useEffect(() => {
        if (reduced) return undefined;
        const t = setTimeout(() => setOn(true), delay);

        return () => clearTimeout(t);
    }, [delay, reduced]);

    return (
        <div
            style={{
                opacity: on ? 1 : 0,
                transform: on ? 'none' : 'translateY(12px)',
                transition: reduced
                    ? 'none'
                    : 'opacity 620ms cubic-bezier(.22,.8,.28,1), transform 620ms cubic-bezier(.22,.8,.28,1)',
                ...style,
            }}
        >
            {children}
        </div>
    );
};

export function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = React.useState(
        () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
    );

    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');

        if (!mq) return undefined;
        const on = () => setReduced(mq.matches);

        mq.addEventListener?.('change', on);
        return () => mq.removeEventListener?.('change', on);
    }, []);

    return reduced;
}

/** Viewport width watcher — drives tab bar vs sidebar without a CSS-only fork. */
export function useIsDesktop(breakpoint: number): boolean {
    const [wide, setWide] = React.useState(() => typeof window !== 'undefined' && window.innerWidth >= breakpoint);

    React.useEffect(() => {
        const on = () => setWide(window.innerWidth >= breakpoint);

        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, [breakpoint]);

    return wide;
}

/**
 * Desktop surface container. The mobile screens are one column with 20px screen
 * padding baked into `Sec`; on desktop that padding would double against the
 * page gutter, so panels own their own padding and sections drop theirs.
 */
export const Panel: React.FC<{
    title?: string;
    right?: React.ReactNode;
    children: React.ReactNode;
    pad?: string;
    style?: React.CSSProperties;
}> = ({ title, right, children, pad = '20px 22px', style }) => (
    <div style={{ background: c.card, border: hair, borderRadius: 18, minWidth: 0, ...style }}>
        {title && (
            <Row style={{ padding: '15px 22px 13px', borderBottom: hair2, justifyContent: 'space-between', gap: 12 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{title}</Meta>
                {right}
            </Row>
        )}
        <div style={{ padding: pad, minWidth: 0 }}>{children}</div>
    </div>
);

/**
 * Fixed-width desktop rail. `flex: '0 0 auto'` plus an explicit width keeps it
 * from being squeezed, while siblings carry `minWidth: 0` so a wide child
 * scrolls inside its own box instead of stretching the page.
 */
export const Rail: React.FC<{
    width: number;
    children: React.ReactNode;
    gap?: number;
    style?: React.CSSProperties;
}> = ({ width, children, gap = 16, style }) => (
    <Col style={{ width, flex: '0 0 auto', minWidth: 0, ...style }} gap={gap}>
        {children}
    </Col>
);

/**
 * Flexible desktop column.
 *
 * `flex: 1 1 420px` rather than `flex: 1`: with a real basis the row wraps a
 * rail underneath instead of crushing the reading column to a 200px ribbon on a
 * 1200px screen. `minWidth: 0` is what stops the opposite failure — a wide child
 * stretching the column past the viewport.
 */
export const Fluid: React.FC<{ children: React.ReactNode; gap?: number; style?: React.CSSProperties }> = ({
    children,
    gap = 16,
    style,
}) => (
    <Col style={{ flex: '1 1 420px', minWidth: 0, ...style }} gap={gap}>
        {children}
    </Col>
);
